<app-page-layout 
  [title]="title"
  [subtitle]="subtitle"
  [heroImage]="heroImage"
  [breadcrumbs]="breadcrumbs"
  [prevPage]="prevPage"
  [nextPage]="nextPage">

  <div class="content-section">
    <h2>Assurance RC Pro Juridique : Protection des Professionnels du Droit</h2>
    <p class="intro-text">
      L'assurance <strong>Responsabilité Civile Professionnelle Juridique</strong> est une couverture 
      spécialisée et obligatoire pour les professions juridiques réglementées. Elle protège contre 
      les conséquences des erreurs de conseil, fautes professionnelles, négligences, ou manquements 
      aux obligations déontologiques qui peuvent engager votre responsabilité civile professionnelle.
    </p>

    <div class="obligation-warning">
      <div class="warning-header">
        <i class="fas fa-gavel"></i>
        <h3>Obligation Légale pour les Professions Juridiques</h3>
      </div>
      <p>
        La RC Pro est <strong>obligatoire</strong> pour toutes les professions juridiques réglementées. 
        L'attestation d'assurance doit être fournie lors de l'inscription à l'Ordre, au Barreau, ou à 
        la Chambre professionnelle, et renouvelée chaque année. L'exercice sans assurance est passible 
        de sanctions disciplinaires et pénales.
      </p>
      <div class="sanctions">
        <strong>Sanctions :</strong> Interdiction d'exercer, radiation, amendes jusqu'à 45 000 €, 
        emprisonnement jusqu'à 6 mois
      </div>
    </div>
  </div>

  <div class="content-section">
    <h2>Professions Juridiques Concernées</h2>

    <div class="professions-juridiques">
      <div class="profession-category">
        <div class="category-header">
          <i class="fas fa-balance-scale"></i>
          <h3>Avocats</h3>
        </div>
        <div class="profession-details">
          <p>
            <strong>Obligation :</strong> Assurance obligatoire pour l'inscription au Barreau 
            (article 98 du décret du 27 novembre 1991).
          </p>
          <ul class="specialites">
            <li><i class="fas fa-check"></i> Avocats généralistes</li>
            <li><i class="fas fa-check"></i> Avocats d'affaires</li>
            <li><i class="fas fa-check"></i> Avocats pénalistes</li>
            <li><i class="fas fa-check"></i> Avocats fiscalistes</li>
            <li><i class="fas fa-check"></i> Avocats spécialisés (famille, immobilier, social, etc.)</li>
            <li><i class="fas fa-check"></i> Collaborateurs et associés</li>
          </ul>
          <div class="plafond-minimum">
            <strong>Plafond minimum obligatoire :</strong> 500 000 € par sinistre et 1 500 000 € par année
          </div>
        </div>
      </div>

      <div class="profession-category">
        <div class="category-header">
          <i class="fas fa-stamp"></i>
          <h3>Notaires</h3>
        </div>
        <div class="profession-details">
          <p>
            <strong>Obligation :</strong> Assurance obligatoire organisée collectivement par la profession 
            via la Caisse de Garantie de Notariat.
          </p>
          <ul class="specialites">
            <li><i class="fas fa-check"></i> Notaires titulaires</li>
            <li><i class="fas fa-check"></i> Notaires associés</li>
            <li><i class="fas fa-check"></i> Notaires salariés</li>
            <li><i class="fas fa-check"></i> Clercs de notaire</li>
          </ul>
          <div class="specificite">
            <strong>Spécificité :</strong> Régime collectif de garantie obligatoire avec cotisation 
            proportionnelle aux recettes de l'office.
          </div>
        </div>
      </div>

      <div class="profession-category">
        <div class="category-header">
          <i class="fas fa-scroll"></i>
          <h3>Huissiers de Justice</h3>
        </div>
        <div class="profession-details">
          <p>
            <strong>Obligation :</strong> Assurance obligatoire pour l'exercice de la profession 
            (article 20 de l'ordonnance du 2 novembre 1945).
          </p>
          <ul class="specialites">
            <li><i class="fas fa-check"></i> Huissiers titulaires</li>
            <li><i class="fas fa-check"></i> Huissiers associés</li>
            <li><i class="fas fa-check"></i> Clercs assermentés</li>
          </ul>
          <div class="plafond-minimum">
            <strong>Plafond minimum :</strong> Variable selon le volume d'activité, généralement 300 000 € à 1 000 000 €
          </div>
        </div>
      </div>

      <div class="profession-category">
        <div class="category-header">
          <i class="fas fa-landmark"></i>
          <h3>Commissaires-Priseurs Judiciaires</h3>
        </div>
        <div class="profession-details">
          <p>
            <strong>Obligation :</strong> Assurance obligatoire pour l'activité de ventes aux enchères 
            et estimations.
          </p>
          <ul class="specialites">
            <li><i class="fas fa-check"></i> Commissaires-priseurs judiciaires</li>
            <li><i class="fas fa-check"></i> Opérateurs de ventes volontaires</li>
            <li><i class="fas fa-check"></i> Experts en objets d'art</li>
          </ul>
        </div>
      </div>

      <div class="profession-category">
        <div class="category-header">
          <i class="fas fa-briefcase"></i>
          <h3>Juristes d'Entreprise et Conseils Juridiques</h3>
        </div>
        <div class="profession-details">
          <p>
            <strong>Statut :</strong> RC Pro fortement recommandée mais non obligatoire (sauf exigence 
            contractuelle de l'employeur ou des clients).
          </p>
          <ul class="specialites">
            <li><i class="fas fa-arrow-right"></i> Juristes d'entreprise</li>
            <li><i class="fas fa-arrow-right"></i> Directeurs juridiques</li>
            <li><i class="fas fa-arrow-right"></i> Consultants en droit</li>
            <li><i class="fas fa-arrow-right"></i> Médiateurs</li>
            <li><i class="fas fa-arrow-right"></i> Arbitres</li>
          </ul>
        </div>
      </div>
    </div>
  </div>

  <div class="content-section">
    <h2>Garanties de la RC Pro Juridique</h2>

    <div class="garanties-juridiques">
      <div class="garantie-principale">
        <div class="garantie-header">
          <i class="fas fa-shield-alt"></i>
          <h3>Garanties Essentielles</h3>
        </div>

        <div class="garantie-item">
          <h4><i class="fas fa-file-contract"></i> Erreurs et Omissions Professionnelles</h4>
          <p>
            Couverture des conséquences d'une erreur de conseil, d'analyse juridique, d'interprétation 
            de la loi, d'omission d'une procédure, ou de non-respect d'un délai légal qui cause un 
            préjudice au client.
          </p>
          <div class="exemples">
            <strong>Exemples :</strong> Erreur dans la rédaction d'un contrat, oubli d'un recours, 
            non-respect d'un délai de prescription, mauvaise qualification juridique, conseil erroné.
          </div>
        </div>

        <div class="garantie-item">
          <h4><i class="fas fa-euro-sign"></i> Dommages Financiers et Immatériels</h4>
          <p>
            Indemnisation des pertes financières subies par le client suite à une faute professionnelle : 
            perte de droits, manque à gagner, préjudice économique, frais supplémentaires engagés.
          </p>
          <div class="plafond-standard">
            Plafond habituel : 500 000 € à 5 000 000 € par sinistre selon le volume d'activité
          </div>
        </div>

        <div class="garantie-item">
          <h4><i class="fas fa-clock"></i> Non-Respect des Délais</h4>
          <p>
            Couverture spécifique pour les conséquences du non-respect des délais de procédure, de 
            recours, d'appel, ou de prescription qui entraînent la perte de droits du client.
          </p>
          <div class="importance">
            <strong>Très fréquent :</strong> Représente une part importante des sinistres en RC Pro juridique
          </div>
        </div>

        <div class="garantie-item">
          <h4><i class="fas fa-user-secret"></i> Violation du Secret Professionnel</h4>
          <p>
            Prise en charge des conséquences de la divulgation involontaire d'informations confidentielles, 
            de violation du secret professionnel, ou de manquements à la confidentialité.
          </p>
        </div>

        <div class="garantie-item">
          <h4><i class="fas fa-money-bill-wave"></i> Détournement ou Vol de Fonds</h4>
          <p>
            <strong>Garantie spécifique professions manipulant des fonds :</strong> Couvre le détournement, 
            vol, ou mauvaise gestion des fonds confiés (notamment pour avocats avec compte CARPA, notaires).
          </p>
          <div class="plafond-fonds">
            Plafond spécifique : 150 000 € à 1 000 000 € selon le volume de fonds manipulés
          </div>
        </div>

        <div class="garantie-item">
          <h4><i class="fas fa-gavel"></i> Défense Juridique et Recours</h4>
          <p>
            Prise en charge des frais d'avocat, d'expertise, de procédure en cas de mise en cause de 
            votre responsabilité devant les tribunaux civils, pénaux, ou disciplinaires (Ordre, Barreau).
          </p>
          <div class="plafond-defense">Plafond : 50 000 € à 150 000 € par sinistre</div>
        </div>

        <div class="garantie-item">
          <h4><i class="fas fa-history"></i> Garantie Rétroactivité</h4>
          <p>
            Couvre les réclamations pour des faits survenus avant la souscription du contrat mais 
            déclarées pendant sa période de validité. Essentiel lors d'un changement d'assureur.
          </p>
        </div>

        <div class="garantie-item">
          <h4><i class="fas fa-calendar-alt"></i> Garantie Postérieure (Après Cessation)</h4>
          <p>
            <strong>Indispensable :</strong> Couvre les réclamations formulées après votre cessation 
            d'activité (retraite, reconversion) pour des faits survenus pendant votre exercice.
          </p>
          <div class="duree-recommandee">Durée recommandée : 5 à 10 ans après cessation</div>
        </div>
      </div>

      <div class="garantie-optionnelle">
        <div class="garantie-header">
          <i class="fas fa-plus-circle"></i>
          <h3>Extensions de Garantie</h3>
        </div>

        <div class="extension-item">
          <div class="extension-title">
            <i class="fas fa-users"></i>
            <h4>Extension Collaborateurs et Stagiaires</h4>
            <span class="cout">+ 100 € à 500 €/personne/an</span>
          </div>
          <p>
            Couverture des collaborateurs, associés, stagiaires, et clercs travaillant au sein de 
            votre cabinet ou structure.
          </p>
        </div>

        <div class="extension-item">
          <div class="extension-title">
            <i class="fas fa-laptop-code"></i>
            <h4>Cyber-responsabilité et Protection des Données</h4>
            <span class="cout">+ 200 € à 1 000 €/an</span>
          </div>
          <p>
            Protection contre les violations de données clients (RGPD), cyberattaques, ransomware, 
            divulgation involontaire de documents confidentiels.
          </p>
        </div>

        <div class="extension-item">
          <div class="extension-title">
            <i class="fas fa-globe-americas"></i>
            <h4>Extension Géographique Internationale</h4>
            <span class="cout">+ 150 € à 800 €/an</span>
          </div>
          <p>
            Couvre l'exercice hors de France pour les dossiers internationaux, arbitrages, conseils 
            à l'export (généralement hors USA/Canada).
          </p>
        </div>

        <div class="extension-item">
          <div class="extension-title">
            <i class="fas fa-handshake"></i>
            <h4>Responsabilité Civile Mandataire Social</h4>
            <span class="cout">+ 300 € à 2 000 €/an</span>
          </div>
          <p>
            Pour les avocats gérants de SCP ou associés : couvre les fautes de gestion, manquements 
            aux obligations sociales/fiscales, décisions préjudiciables.
          </p>
        </div>

        <div class="extension-item">
          <div class="extension-title">
            <i class="fas fa-umbrella"></i>
            <h4>Augmentation des Plafonds</h4>
            <span class="cout">+ 100 € à 1 000 €/an</span>
          </div>
          <p>
            Possibilité d'augmenter les plafonds de garantie au-delà des montants standards pour les 
            dossiers à enjeux financiers élevés.
          </p>
        </div>
      </div>
    </div>
  </div>

  <div class="content-section">
    <h2>Tarifs de la RC Pro Juridique par Profession</h2>

    <div class="tarifs-juridiques">
      <div class="tarif-profession">
        <div class="profession-header">
          <i class="fas fa-balance-scale"></i>
          <h3>Avocats</h3>
        </div>
        <div class="tarif-details">
          <div class="tarif-base">
            <div class="tarif-montant">1 200 € - 8 000 €</div>
            <div class="tarif-periode">par an</div>
          </div>
          <div class="tarif-ventilation">
            <h4>Tarifs selon la spécialité :</h4>
            <ul class="tarif-list">
              <li>
                <span class="specialite">Avocat généraliste (débutant) :</span>
                <span class="prix">1 200 € - 2 500 €/an</span>
              </li>
              <li>
                <span class="specialite">Avocat généraliste (confirmé) :</span>
                <span class="prix">2 000 € - 4 000 €/an</span>
              </li>
              <li>
                <span class="specialite">Droit de la famille, pénal :</span>
                <span class="prix">1 800 € - 3 500 €/an</span>
              </li>
              <li>
                <span class="specialite">Droit immobilier, social :</span>
                <span class="prix">2 200 € - 4 500 €/an</span>
              </li>
              <li>
                <span class="specialite">Avocat d'affaires, fiscal :</span>
                <span class="prix">3 500 € - 8 000 €/an</span>
              </li>
              <li>
                <span class="specialite">Droit commercial, M&A :</span>
                <span class="prix">4 000 € - 10 000 €+/an</span>
              </li>
            </ul>
          </div>
          <div class="facteurs-tarif">
            <h4>Critères d'évaluation :</h4>
            <ul>
              <li><i class="fas fa-check"></i> Chiffre d'affaires du cabinet</li>
              <li><i class="fas fa-check"></i> Nombre d'associés et collaborateurs</li>
              <li><i class="fas fa-check"></i> Spécialité et domaines d'intervention</li>
              <li><i class="fas fa-check"></i> Enjeux financiers moyens des dossiers</li>
              <li><i class="fas fa-check"></i> Historique de sinistres</li>
              <li><i class="fas fa-check"></i> Ancienneté au Barreau</li>
            </ul>
          </div>
        </div>
      </div>

      <div class="tarif-profession">
        <div class="profession-header">
          <i class="fas fa-stamp"></i>
          <h3>Notaires</h3>
        </div>
        <div class="tarif-details">
          <div class="tarif-info">
            <p>
              <strong>Régime particulier :</strong> Assurance collective obligatoire via la Caisse de 
              Garantie de Notariat. La cotisation est calculée en fonction des recettes de l'office 
              notarial (pourcentage du chiffre d'affaires).
            </p>
          </div>
          <div class="tarif-base">
            <div class="tarif-montant">2 000 € - 15 000 €+</div>
            <div class="tarif-periode">par an selon le CA de l'office</div>
          </div>
          <div class="tarif-ventilation">
            <h4>Tarifs indicatifs :</h4>
            <ul class="tarif-list">
              <li>
                <span class="specialite">Petit office rural (CA < 200 000 €) :</span>
                <span class="prix">2 000 € - 4 000 €/an</span>
              </li>
              <li>
                <span class="specialite">Office moyen (CA 200 000 € - 500 000 €) :</span>
                <span class="prix">4 000 € - 8 000 €/an</span>
              </li>
              <li>
                <span class="specialite">Grand office (CA 500 000 € - 1 M€) :</span>
                <span class="prix">8 000 € - 15 000 €/an</span>
              </li>
              <li>
                <span class="specialite">Très grand office (CA > 1 M€) :</span>
                <span class="prix">15 000 € - 50 000 €+/an</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div class="tarif-profession">
        <div class="profession-header">
          <i class="fas fa-scroll"></i>
          <h3>Huissiers de Justice</h3>
        </div>
        <div class="tarif-details">
          <div class="tarif-base">
            <div class="tarif-montant">1 500 € - 6 000 €</div>
            <div class="tarif-periode">par an</div>
          </div>
          <div class="tarif-ventilation">
            <h4>Tarifs selon l'activité :</h4>
            <ul class="tarif-list">
              <li>
                <span class="specialite">Huissier débutant :</span>
                <span class="prix">1 500 € - 2 500 €/an</span>
              </li>
              <li>
                <span class="specialite">Activité standard :</span>
                <span class="prix">2 500 € - 4 000 €/an</span>
              </li>
              <li>
                <span class="specialite">Étude importante :</span>
                <span class="prix">4 000 € - 6 000 €/an</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div class="tarif-profession">
        <div class="profession-header">
          <i class="fas fa-briefcase"></i>
          <h3>Juristes et Conseils Juridiques</h3>
        </div>
        <div class="tarif-details">
          <div class="tarif-base">
            <div class="tarif-montant">800 € - 4 000 €</div>
            <div class="tarif-periode">par an</div>
          </div>
          <div class="tarif-ventilation">
            <h4>Tarifs selon le statut :</h4>
            <ul class="tarif-list">
              <li>
                <span class="specialite">Consultant indépendant :</span>
                <span class="prix">800 € - 2 000 €/an</span>
              </li>
              <li>
                <span class="specialite">Cabinet de conseil juridique :</span>
                <span class="prix">1 500 € - 3 500 €/an</span>
              </li>
              <li>
                <span class="specialite">Médiateur, arbitre :</span>
                <span class="prix">1 000 € - 3 000 €/an</span>
              </li>
              <li>
                <span class="specialite">Directeur juridique (conseil externe) :</span>
                <span class="prix">2 000 € - 4 000 €/an</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div class="content-section">
    <h2>Exemples de Sinistres en RC Pro Juridique</h2>

    <div class="sinistres-juridiques">
      <div class="sinistre-card">
        <div class="sinistre-header">
          <i class="fas fa-balance-scale"></i>
          <h3>Avocat - Non-respect d'un Délai d'Appel</h3>
        </div>
        <div class="sinistre-content">
          <h4>Situation :</h4>
          <p>
            Un avocat oublie de faire appel d'un jugement défavorable dans les délais légaux (1 mois). 
            Le client perd définitivement son droit de contester la décision qui le condamne à 
            verser 200 000 € de dommages-intérêts.
          </p>
          <h4>Conséquences :</h4>
          <ul>
            <li><i class="fas fa-euro-sign"></i> Indemnisation perte de chance : 150 000 €</li>
            <li><i class="fas fa-euro-sign"></i> Préjudice moral : 20 000 €</li>
            <li><i class="fas fa-euro-sign"></i> Frais de procédure : 15 000 €</li>
          </ul>
          <div class="sinistre-total">
            <strong>Total pris en charge par la RC Pro :</strong> 185 000 €
          </div>
        </div>
      </div>

      <div class="sinistre-card">
        <div class="sinistre-header">
          <i class="fas fa-stamp"></i>
          <h3>Notaire - Erreur dans un Acte de Vente</h3>
        </div>
        <div class="sinistre-content">
          <h4>Situation :</h4>
          <p>
            Lors de la vente d'un bien immobilier, le notaire omet de signaler une servitude de 
            passage grevant le terrain. L'acquéreur découvre cette servitude après l'achat et 
            subit une moins-value importante sur son bien.
          </p>
          <h4>Conséquences :</h4>
          <ul>
            <li><i class="fas fa-euro-sign"></i> Moins-value du bien : 80 000 €</li>
            <li><i class="fas fa-euro-sign"></i> Frais de contentieux : 12 000 €</li>
            <li><i class="fas fa-euro-sign"></i> Préjudice moral : 8 000 €</li>
          </ul>
          <div class="sinistre-total">
            <strong>Total pris en charge :</strong> 100 000 €
          </div>
        </div>
      </div>

      <div class="sinistre-card">
        <div class="sinistre-header">
          <i class="fas fa-balance-scale"></i>
          <h3>Avocat d'Affaires - Erreur dans un Contrat</h3>
        </div>
        <div class="sinistre-content">
          <h4>Situation :</h4>
          <p>
            Un avocat d'affaires rédige un contrat de cession d'entreprise avec une clause de 
            garantie d'actif-passif mal formulée. Lors d'un litige ultérieur, la clause est 
            jugée inopposable, exposant son client à payer 500 000 € de passifs non déclarés.
          </p>
          <h4>Conséquences :</h4>
          <ul>
            <li><i class="fas fa-euro-sign"></i> Perte de droits (garantie) : 500 000 €</li>
            <li><i class="fas fa-euro-sign"></i> Expertise et frais de procédure : 40 000 €</li>
          </ul>
          <div class="sinistre-total">
            <strong>Total pris en charge :</strong> 540 000 €
          </div>
        </div>
      </div>

      <div class="sinistre-card">
        <div class="sinistre-header">
          <i class="fas fa-scroll"></i>
          <h3>Huissier - Signification Irrégulière</h3>
        </div>
        <div class="sinistre-content">
          <h4>Situation :</h4>
          <p>
            Un huissier effectue une signification d'assignation de manière irrégulière (mauvaise 
            adresse, vice de forme). La procédure est annulée pour vice de forme, entraînant la 
            prescription du litige et la perte des droits du créancier.
          </p>
          <h4>Conséquences :</h4>
          <ul>
            <li><i class="fas fa-euro-sign"></i> Créance perdue par prescription : 45 000 €</li>
            <li><i class="fas fa-euro-sign"></i> Frais de nouvelle procédure : 8 000 €</li>
            <li><i class="fas fa-euro-sign"></i> Préjudice moral : 5 000 €</li>
          </ul>
          <div class="sinistre-total">
            <strong>Total pris en charge :</strong> 58 000 €
          </div>
        </div>
      </div>

      <div class="sinistre-card">
        <div class="sinistre-header">
          <i class="fas fa-briefcase"></i>
          <h3>Consultant Juridique - Conseil Erroné</h3>
        </div>
        <div class="sinistre-content">
          <h4>Situation :</h4>
          <p>
            Un consultant en droit social conseille à une entreprise de licencier un salarié pour 
            motif économique sans respecter la procédure. Le salarié obtient gain de cause aux 
            Prud'hommes, l'entreprise doit le réintégrer et verser des dommages-intérêts.
          </p>
          <h4>Conséquences :</h4>
          <ul>
            <li><i class="fas fa-euro-sign"></i> Dommages-intérêts salarié : 60 000 €</li>
            <li><i class="fas fa-euro-sign"></i> Frais prud'homaux : 15 000 €</li>
            <li><i class="fas fa-euro-sign"></i> Coût réintégration et désorganisation : 25 000 €</li>
          </ul>
          <div class="sinistre-total">
            <strong>Total pris en charge :</strong> 100 000 €
          </div>
        </div>
      </div>

      <div class="sinistre-card">
        <div class="sinistre-header">
          <i class="fas fa-user-secret"></i>
          <h3>Avocat - Violation du Secret Professionnel</h3>
        </div>
        <div class="sinistre-content">
          <h4>Situation :</h4>
          <p>
            Un avocat divulgue involontairement des informations confidentielles sur un dossier 
            sensible lors d'une interview médiatique. Le client subit un préjudice d'image important 
            et perd un contrat majeur.
          </p>
          <h4>Conséquences :</h4>
          <ul>
            <li><i class="fas fa-euro-sign"></i> Perte du contrat : 300 000 €</li>
            <li><i class="fas fa-euro-sign"></i> Préjudice d'image et réputation : 100 000 €</li>
            <li><i class="fas fa-euro-sign"></i> Frais de gestion de crise : 20 000 €</li>
          </ul>
          <div class="sinistre-total">
            <strong>Total pris en charge :</strong> 420 000 €
          </div>
        </div>
      </div>
    </div>
  </div>

  <div class="content-section">
    <h2>Comment Réduire le Coût de votre RC Pro Juridique ?</h2>

    <div class="reduction-tips">
      <div class="tip-item">
        <div class="tip-icon">
          <i class="fas fa-balance-scale"></i>
        </div>
        <h3>1. Comparez les Offres Spécialisées</h3>
        <p>
          Faites jouer la concurrence entre assureurs spécialisés dans les professions juridiques 
          (Allianz, AXA, MMA, Generali, Hiscox). Les tarifs peuvent varier de 30% à 50%.
        </p>
        <div class="tip-saving">Économie : 20% à 40%</div>
      </div>

      <div class="tip-item">
        <div class="tip-icon">
          <i class="fas fa-users"></i>
        </div>
        <h3>2. Contrats de Groupe via Ordre/Barreau</h3>
        <p>
          Adhérez aux contrats collectifs négociés par votre Ordre, Barreau, ou syndicat professionnel 
          qui offrent des conditions avantageuses et des tarifs préférentiels.
        </p>
        <div class="tip-saving">Économie : 15% à 30%</div>
      </div>

      <div class="tip-item">
        <div class="tip-icon">
          <i class="fas fa-graduation-cap"></i>
        </div>
        <h3>3. Tarifs Jeunes Professionnels</h3>
        <p>
          Si vous débutez, bénéficiez des réductions accordées aux jeunes inscrits (3 à 5 premières 
          années) avec des tarifs progressifs selon l'ancienneté.
        </p>
        <div class="tip-saving">Économie : 30% à 50% les premières années</div>
      </div>

      <div class="tip-item">
        <div class="tip-icon">
          <i class="fas fa-sliders-h"></i>
        </div>
        <h3>4. Adaptez Plafonds et Franchise</h3>
        <p>
          Choisissez des plafonds adaptés à vos dossiers réels (respectez les minimums obligatoires). 
          Une franchise raisonnable (1 000 € à 3 000 €) réduit la prime de 10% à 20%.
        </p>
        <div class="tip-saving">Économie : 10% à 20%</div>
      </div>

      <div class="tip-item">
        <div class="tip-icon">
          <i class="fas fa-clipboard-check"></i>
        </div>
        <h3>5. Procédures Internes Rigoureuses</h3>
        <p>
          Mettez en place un système de gestion des délais (échéancier, alertes), double contrôle 
          des actes, relecture systématique des contrats, validation par associé senior.
        </p>
        <div class="tip-saving">Impact : Réduction sinistres et primes futures</div>
      </div>

      <div class="tip-item">
        <div class="tip-icon">
          <i class="fas fa-file-alt"></i>
        </div>
        <h3>6. Traçabilité et Documentation</h3>
        <p>
          Conservez tous les échanges clients (mails, courriers, notes d'entretien), établissez des 
          comptes-rendus systématiques, faites signer les mandats et conventions d'honoraires.
        </p>
        <div class="tip-saving">Impact : Défense facilitée en cas de litige</div>
      </div>

      <div class="tip-item">
        <div class="tip-icon">
          <i class="fas fa-graduation-cap"></i>
        </div>
        <h3>7. Formation Continue</h3>
        <p>
          Participez régulièrement aux formations obligatoires et continues, restez à jour des 
          évolutions législatives et jurisprudentielles, abonnez-vous aux revues juridiques.
        </p>
        <div class="tip-saving">Impact : Prévention des erreurs</div>
      </div>

      <div class="tip-item">
        <div class="tip-icon">
          <i class="fas fa-laptop-code"></i>
        </div>
        <h3>8. Outils de Gestion Professionnels</h3>
        <p>
          Investissez dans un logiciel de gestion de cabinet performant avec gestion automatisée 
          des délais, alertes, suivi des dossiers, modèles d'actes sécurisés.
        </p>
        <div class="tip-saving">Impact : Réduction erreurs humaines</div>
      </div>
    </div>
  </div>

  <div class="content-section">
    <h2>Questions Fréquentes (FAQ)</h2>

    <div class="faq-container">
      <div class="faq-item">
        <h3><i class="fas fa-question-circle"></i> Puis-je exercer sans RC Pro en tant qu'avocat collaborateur ?</h3>
        <p>
          Non. Même en tant que collaborateur salarié, vous devez avoir votre propre RC Pro ou être 
          couvert par le contrat du cabinet (vérifiez que vous êtes nommément désigné dans le contrat 
          du cabinet et demandez une attestation).
        </p>
      </div>

      <div class="faq-item">
        <h3><i class="fas fa-question-circle"></i> La RC Pro couvre-t-elle mes stagiaires et collaborateurs ?</h3>
        <p>
          Cela dépend de votre contrat. Généralement, il faut souscrire une extension spécifique pour 
          les collaborateurs. Les stagiaires sont parfois couverts automatiquement s'ils travaillent 
          sous votre supervision directe.
        </p>
      </div>

      <div class="faq-item">
        <h3><i class="fas fa-question-circle"></i> Combien de temps après la fin d'un dossier puis-je être poursuivi ?</h3>
        <p>
          Le délai de prescription est de <strong>5 ans</strong> à compter de la découverte du dommage 
          (et non de la faute). D'où l'importance de la garantie postérieure après cessation d'activité.
        </p>
      </div>

      <div class="faq-item">
        <h3><i class="fas fa-question-circle"></i> Que faire si je découvre une erreur dans un dossier ?</h3>
        <p>
          1) N'attendez pas : prévenez immédiatement votre client et votre assureur.<br>
          2) Tentez de corriger l'erreur si c'est encore possible (recours, régularisation).<br>
          3) Ne cachez rien à votre assureur : toute réticence peut entraîner la déchéance de garantie.<br>
          4) Documentez toutes vos démarches de correction.
        </p>
      </div>

      <div class="faq-item">
        <h3><i class="fas fa-question-circle"></i> Mon assurance couvre-t-elle les sanctions disciplinaires ?</h3>
        <p>
          La RC Pro couvre la défense devant les instances disciplinaires (Ordre, Barreau) mais ne 
          prend jamais en charge les sanctions elles-mêmes (amendes, suspensions) ni les amendes pénales.
        </p>
      </div>

      <div class="faq-item">
        <h3><i class="fas fa-question-circle"></i> Puis-je changer d'assureur en cours d'année ?</h3>
        <p>
          Oui, grâce à la loi Hamon, vous pouvez résilier à tout moment après la première année. 
          Attention : vérifiez la rétroactivité du nouveau contrat pour couvrir les faits antérieurs 
          et souscrivez une garantie subséquente auprès de l'ancien assureur si nécessaire.
        </p>
      </div>

      <div class="faq-item">
        <h3><i class="fas fa-question-circle"></i> La RC Pro couvre-t-elle les conflits d'intérêts ?</h3>
        <p>
          Généralement non ou partiellement. Les fautes déontologiques graves (conflit d'intérêts 
          avéré, violation intentionnelle des règles professionnelles) peuvent être exclues de la 
          garantie. Lisez attentivement les exclusions de votre contrat.
        </p>
      </div>

      <div class="faq-item">
        <h3><i class="fas fa-question-circle"></i> Que se passe-t-il si mon assureur refuse de m'assurer ?</h3>
        <p>
          En cas de refus multiple (sinistralité trop importante), vous pouvez saisir le Bureau Central 
          de Tarification (BCT) qui obligera un assureur à vous couvrir moyennant une majoration de 
          prime. C'est la solution de dernier recours.
        </p>
      </div>
    </div>
  </div>

  <div class="cta-section">
    <h2>Trouvez la Meilleure RC Pro Juridique</h2>
    <p>
      Protégez votre exercice professionnel et respectez vos obligations déontologiques. Comparez 
      les offres des meilleurs assureurs spécialisés en professions juridiques et obtenez votre 
      devis personnalisé en quelques clics.
    </p>
    <a href="/comparaison" class="cta-button">
      <i class="fas fa-gavel"></i> Comparer les RC Pro Juridiques
    </a>
  </div>

</app-page-layout>
